/*
 * A collection's filters, worked out on the page (sections/ms-collection.liquid): its kinds (tags, from the collection's
 * "<handle>-type" menu), Size, Colour, Price and In stock only, and the sort. Each card carries what they need as data
 * attributes, so a choice answers at once with no reload. Sold out always sorts last. The state lives in the address
 * (?kind=apparel&size=M&stock=1&sort=price-ascending), so a filtered page can be shared. The rest of the collection loads
 * as you scroll (Shopify gives 50 to a page), and filters apply to it as it lands.
 */

const SORTS = ["manual", "created-descending", "price-ascending", "price-descending", "title-ascending"];

/** The filters in an address's query. */
export function parseState(search) {
  const q = new URLSearchParams(search || "");
  const sort = q.get("sort");
  return {
    kind: q.get("kind") || "",
    sizes: q.getAll("size"),
    colours: q.getAll("colour"),
    price: q.get("price") || "",
    stock: q.get("stock") === "1",
    sort: SORTS.includes(sort) ? sort : "manual",
  };
}

/** The address's query for a state ("" when nothing's set). */
export function stateSearch(s) {
  const q = new URLSearchParams();
  if (s.kind) q.set("kind", s.kind);
  for (const v of s.sizes) q.append("size", v);
  for (const v of s.colours) q.append("colour", v);
  if (s.price) q.set("price", s.price);
  if (s.stock) q.set("stock", "1");
  if (s.sort && s.sort !== "manual") q.set("sort", s.sort);
  const out = q.toString();
  return out ? `?${out}` : "";
}

/** Which price band a price (in cents) falls in: under $25, $25 to $50, $50 to $100, $100 or more. */
export function priceBand(cents) {
  const d = cents / 100;
  if (d < 25) return "0-25";
  if (d < 50) return "25-50";
  if (d < 100) return "50-100";
  return "100-";
}

/** Whether a card passes every filter that's set (sizes and colours: any of the ones picked). */
export function matches(c, s) {
  if (s.kind && !c.tags.includes(s.kind)) return false;
  if (s.sizes.length && !s.sizes.some((v) => c.sizes.includes(v))) return false;
  if (s.colours.length && !s.colours.some((v) => c.colours.includes(v))) return false;
  if (s.price && priceBand(c.price) !== s.price) return false;
  if (s.stock && !c.available) return false;
  return true;
}

/** Cards in the sort's order, sold out last; ties keep the collection's own order. */
export function ordered(cards, sort) {
  const by = {
    manual: (a, b) => a.index - b.index,
    "created-descending": (a, b) => b.created - a.created || a.index - b.index,
    "price-ascending": (a, b) => a.price - b.price || a.index - b.index,
    "price-descending": (a, b) => b.price - a.price || a.index - b.index,
    "title-ascending": (a, b) => (a.title || "").localeCompare(b.title || "") || a.index - b.index,
  }[sort] || ((a, b) => a.index - b.index);
  return [...cards].sort((a, b) => (a.available === b.available ? by(a, b) : a.available ? -1 : 1));
}

const read = (el) => ({
  el,
  tags: (el.dataset.tags || "").split("|").filter(Boolean),
  sizes: (el.dataset.sizes || "").split("|").filter(Boolean),
  colours: (el.dataset.colours || "").split("|").filter(Boolean),
  price: Number(el.dataset.price) || 0,
  available: el.dataset.available === "1",
  created: Number(el.dataset.created) || 0,
  index: Number(el.dataset.index) || 0,
  title: el.querySelector(".t-card")?.textContent || "",
});

const Base = typeof HTMLElement === "undefined" ? class {} : HTMLElement;

class MsCollection extends Base {
  connectedCallback() {
    this.grid = this.querySelector("[data-grid]");
    this.state = parseState(location.search);
    this.cards = [...this.grid.querySelectorAll(".coll-item")].map(read);
    this.addEventListener("click", (e) => this.onClick(e));
    this.addEventListener("change", (e) => this.onChange(e));
    // A popover closes when another opens or on a click elsewhere.
    this.addEventListener("toggle", (e) => { if (e.target.open) this.querySelectorAll("details.fp-wrap[open]").forEach((d) => { if (d !== e.target) d.open = false; }); }, true);
    document.addEventListener("click", (e) => { const d = e.target.closest("details.fp-wrap"); if (!d || e.target === d) this.querySelectorAll("details.fp-wrap[open]").forEach((x) => { x.open = false; }); });
    this.apply(false);
    this.more();
  }

  onClick(e) {
    const kind = e.target.closest("[data-kind]");
    if (kind) { this.state.kind = kind.dataset.kind; this.apply(); return; }
    const opt = e.target.closest("[data-group] [data-value]");
    if (opt) {
      const g = opt.closest("[data-group]").dataset.group;
      if (g === "price") this.state.price = this.state.price === opt.dataset.value ? "" : opt.dataset.value;
      else {
        const key = g === "size" ? "sizes" : "colours";
        const v = opt.dataset.value;
        this.state[key] = this.state[key].includes(v) ? this.state[key].filter((x) => x !== v) : [...this.state[key], v];
      }
      this.apply();
      return;
    }
    const clear = e.target.closest("[data-clear]");
    if (clear) {
      const g = clear.dataset.clear;
      if (g === "size") this.state.sizes = [];
      else if (g === "colour") this.state.colours = [];
      else if (g === "price") this.state.price = "";
      else if (g === "stock") this.state.stock = false;
      else if (g === "kind") this.state.kind = "";
      else this.state = { ...parseState(""), sort: this.state.sort };
      this.apply();
      return;
    }
    if (e.target.closest("[data-done]")) e.target.closest("details").open = false;
    const sw = e.target.closest("[data-stock]");
    if (sw) { this.state.stock = !this.state.stock; this.apply(); }
  }

  onChange(e) {
    if (e.target.matches("[data-sort]")) { this.state.sort = e.target.value; this.apply(); }
  }

  /** Shows the cards that pass, in order, and repaints the pills, the count and the chips. */
  apply(push = true) {
    const s = this.state;
    const shown = ordered(this.cards.filter((c) => matches(c, s)), s.sort);
    const set = new Set(shown.map((c) => c.el));
    for (const c of this.cards) c.el.hidden = !set.has(c.el);
    for (const c of shown) this.grid.append(c.el);
    const total = this.cards.length;
    this.querySelectorAll("[data-count]").forEach((n) => { n.textContent = shown.length === total ? `${total} ${total === 1 ? n.dataset.one || "product" : n.dataset.many || "products"}` : `${shown.length} of ${total}`; });
    this.querySelectorAll("[data-kind]").forEach((b) => { const on = b.dataset.kind === s.kind; b.classList.toggle("on", on); b.setAttribute("aria-pressed", String(on)); });
    const picked = { size: s.sizes, colour: s.colours, price: s.price ? [s.price] : [] };
    this.querySelectorAll("[data-group]").forEach((g) => {
      const vals = picked[g.dataset.group] || [];
      g.querySelectorAll("[data-value]").forEach((o) => { const on = vals.includes(o.dataset.value); o.classList.toggle("on", on); o.setAttribute("aria-pressed", String(on)); });
      const pill = g.closest("details")?.querySelector(".fp");
      if (pill) {
        pill.classList.toggle("on", vals.length > 0);
        const label = pill.querySelector("[data-pill-label]");
        if (label) label.textContent = pill.dataset.name + (vals.length === 1 ? ` · ${g.querySelector(`[data-value="${CSS.escape(vals[0])}"]`)?.dataset.label || vals[0]}` : vals.length > 1 ? ` · ${vals.length}` : "");
      }
      const go = g.closest("details")?.querySelector("[data-done]");
      if (go) go.textContent = `Show ${shown.length}`;
    });
    this.querySelectorAll("[data-stock]").forEach((b) => { b.classList.toggle("on", s.stock); b.setAttribute("aria-checked", String(s.stock)); });
    const sort = this.querySelector("[data-sort]");
    if (sort) sort.value = s.sort;
    this.paintChips();
    const empty = this.querySelector("[data-none]");
    if (empty) empty.hidden = shown.length > 0;
    if (push) history.replaceState(null, "", location.pathname + stateSearch(s) + location.hash);
  }

  /** The chips for what's set, each with its own x, and Clear all. */
  paintChips() {
    const box = this.querySelector("[data-active]");
    if (!box) return;
    const s = this.state;
    const x = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>';
    const chip = (label, attrs) => `<button class="chip on" type="button" ${attrs} aria-label="Remove ${label}">${label}${x}</button>`;
    const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
    const kindLabel = this.querySelector(`[data-kind="${CSS.escape(s.kind)}"]`)?.textContent || s.kind;
    const priceLabel = this.querySelector(`[data-group="price"] [data-value="${CSS.escape(s.price)}"]`)?.dataset.label || s.price;
    let out = "";
    if (s.kind) out += chip(esc(kindLabel), 'data-clear="kind"');
    for (const v of s.sizes) out += chip(esc(v), `data-group-chip data-value-chip="size:${esc(v)}"`);
    for (const v of s.colours) out += chip(esc(v), `data-group-chip data-value-chip="colour:${esc(v)}"`);
    if (s.price) out += chip(esc(priceLabel), 'data-clear="price"');
    if (s.stock) out += chip("In stock", 'data-clear="stock"');
    box.innerHTML = out ? out + '<button class="t-link" type="button" data-clear="all">Clear all</button>' : "";
    box.hidden = !out;
    box.querySelectorAll("[data-value-chip]").forEach((b) => b.addEventListener("click", () => {
      const [g, v] = b.dataset.valueChip.split(/:(.*)/s);
      const key = g === "size" ? "sizes" : "colours";
      this.state[key] = this.state[key].filter((y) => y !== v);
      this.apply();
    }));
  }

  /** Loads the collection's next pages as you near the end, and filters them as they land. */
  more() {
    const next = this.querySelector("[data-next]");
    if (!next) return;
    const io = new IntersectionObserver(async (seen) => {
      if (!seen.some((e) => e.isIntersecting) || this.loading) return;
      this.loading = true;
      try {
        const r = await fetch(next.dataset.next, { headers: { accept: "text/html" } });
        const doc = new DOMParser().parseFromString(await r.text(), "text/html");
        const items = [...doc.querySelectorAll(".coll-item")];
        const base = this.cards.length;
        items.forEach((el, i) => { el.dataset.index = String(base + i); this.grid.append(el); this.cards.push(read(el)); });
        const after = doc.querySelector("[data-next]")?.dataset.next;
        if (after) next.dataset.next = after; else { io.disconnect(); next.remove(); }
        this.apply(false);
      } catch { io.disconnect(); next.hidden = false; }
      this.loading = false;
    }, { rootMargin: "800px 0px" });
    io.observe(next);
  }
}

if (typeof customElements !== "undefined" && !customElements.get("ms-collection")) customElements.define("ms-collection", MsCollection);
